<template>
  <div>
    <tool-bar
      :title="toolbarTitle"
      :sub-title="toolbarSubTitle"
      :right-menu-actions="toolbarActionsList"
      @onActionSelected="onToolbarActionSelected"
      :close-action-navigation-activity="closeActionNavigationActivity"
      ref="refToolBar"
    ></tool-bar>

    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false"
    ></error-alert-item>

    <refresh-item
      :show="showRefreshItem"
      @onRefresh="fetchInitialData"
    ></refresh-item>

    <confirmation-dialog
      :show="showStageCommentDialog"
      :confirmation-message="stageComment"
      confirm-text="Continue"
      cancel-text="Dismiss"
      @onConfirm="openTaskAndHideCommentDialog"
      @onNotConfirm="dismissStageComment"
    ></confirmation-dialog>

    <redirecting-message-dialog
      :show="showRedirectMessageDialog"
      ref="redirectMessageDialog"
      @onRedirect="onRedirectPartnerApp"
      @onRedirectionCancelled="onRedirectionCancelled"
    ></redirecting-message-dialog>

    <wxc-countdown
      v-if="autoRefreshOn"
      v-show="false"
      :time="autoRefreshTime"
      @wxcOnComplete="onAutoRefreshTimeFinished"
    ></wxc-countdown>

    <div v-if="showUI" class="margin-top-20 margin-bottom-20 margin-left-10 margin-right-10">
      <loan-summary
        :ez-loan="ezLoan"
        :merchant-i-d="merchantID"
      ></loan-summary>

      <wxc-button
          v-if="showGoToParentLoanButton"
          text="Go to Parent Loan"
          class="horizontal-center margin-top-20"
          type="blue"
          size="full"
          @wxcButtonClicked="onGoToParentLoan">
      </wxc-button>

      <wxc-button
          v-if="showGoToCoApplicantsOrGuarantorsLoansButton"
          text="Guarantors/Co-Applicant Loans"
          class="horizontal-center margin-top-20"
          type="blue"
          size="full"
          @wxcButtonClicked="onGoToCoApplicantsOrGuarantorsLoans">
      </wxc-button>

      <div class="margin-top-10">
        <div v-if="needToShowNextActions" class="margin-top-40">
          <weex-text class="font-size-base text-color-gray">Next actions</weex-text>
          <div v-for="(loanTask,index) in pendingLoanTasks" :key="loanTask.name + index">
            <pending-loan-task-item
              class="margin-top-20 item-cursor"
              :loan-task="loanTask"
              @openTask="openTaskOrShowComment"
            ></pending-loan-task-item>
          </div>
        </div>

        <div v-if="pendingManualTasks" class="margin-top-40">
          <weex-text class="font-size-base text-color-gray">Back Office Verification Tasks
          </weex-text>
          <weex-text class="margin-top-5 margin-left-10 font-size-base text-color-black">
            {{ pendingManualTasks }}
          </weex-text>
        </div>

        <div v-if="needToShowRepeatableActions" class="margin-top-40">
          <weex-text class="font-size-base text-color-gray">Update actions</weex-text>
          <div v-for="(loanTask,index) in repeatableCompletedTasks" :key="loanTask.name + index">
            <repeatable-completed-loan-task-item
              class="margin-top-20 item-cursor"
              :loan-task="loanTask"
              @openTask="openTaskOrShowComment"
            ></repeatable-completed-loan-task-item>
          </div>
        </div>

        <div v-if="needToShowNonRepeatableActions" class="margin-top-40">
          <weex-text class="font-size-base text-color-gray">Completed</weex-text>
          <div v-for="(loanTask,index) in nonRepeatableCompletedTasks" :key="loanTask.name + index">
            <non-repeatable-completed-loan-task-item
              class="margin-top-20"
              :loan-task="loanTask"
            ></non-repeatable-completed-loan-task-item>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WxcCountdown from "../../../../weex-ui/packages/wxc-countdown/index";
import WxcButton from "../../../../weex-ui/packages/wxc-button/index";
import ToolBar from "../../layout/ToolBar";
import Loading from "../../layout/Loading";
import LoanSummary from "../../layout/LoanSummary";
import ErrorAlertItem from "../../layout/ErrorAlertItem";
import PendingLoanTaskItem from "../../layout/PendingLoanTaskItem";
import RepeatableCompletedLoanTaskItem from "../../layout/RepeatableCompletedLoanTaskItem";
import NonRepeatableCompletedLoanTaskItem from "../../layout/NonRepeatableCompletedLoanTaskItem";
import EzUtils from "../../../utils/EzUtils";
import LoanTasksUtility from "../../../utils/LoanTasksUtility";
import ActivityNavigator, { Activities } from "../../../utils/ActivityNavigator";
import DataSource from "../../../data/DataSource";
import PrefManager from "../../../data/model/PrefManager";
import PartnerAppParam from "../../../data/model/PartnerAppParam";
import LoanScopeDataHolder from "../../../data/model/LoanScopeDataHolder";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import { TaskStatus } from "../../../utils/TaskStatus";
import { ToolbarMenuItems } from "../../../utils/ToolbarMenuItems";
import RedirectingMessageDialog from "../../dialogs/RedirectingMessageDialog";
import RefreshItem from "../../layout/RefreshItem";
import { RolesConstants } from "../../../utils/RolesConstants";
import { DataTables } from "../../../utils/DataTables";
import AssociatedEntityDataHolder from "@/data/model/AssociatedEntityDataHolder";
import { AssociatedEntityType } from "@/components/Constants";
import EzLoanUtil from "@/utils/EzLoanUtil";

const AUTO_REFRESH_TIME = 10 * 1000; // 10 secs

export default {
  name: "LoanDetailsActivity",
  components: {
    ErrorAlertItem,
    Loading,
    ToolBar,
    LoanSummary,
    PendingLoanTaskItem,
    NonRepeatableCompletedLoanTaskItem,
    RepeatableCompletedLoanTaskItem,
    WxcCountdown,
    WxcButton,
    ConfirmationDialog,
    RedirectingMessageDialog,
    RefreshItem
  },

  data: () => ({
    toolbarTitle: "Loan Details",
    toolbarSubTitle: "",
    ezLoan: {},
    pendingLoanTasks: [],
    repeatableCompletedTasks: [],
    nonRepeatableCompletedTasks: [],
    loading: false,
    errorAlertEvent: false,
    networkError: {},
    showUI: false,
    autoRefreshTime: "",
    autoRefreshOn: false,
    showStageCommentDialog: false,
    showRedirectMessageDialog: false,
    showRefreshItem: false,
    fetchingDataFirstTime: false,
    stageComment: "",
    lenderRedirectUrl: "",
    merchantID: "",
    pendingManualTasks: null,
    showGoToCoApplicantsOrGuarantorsLoansButton: false,
    showGoToParentLoanButton: false
  }),

  methods: {
    resetData() {
      this.toolbarTitle = "Loan Details";
      this.toolbarSubTitle = "";
      this.ezLoan = {};
      this.pendingLoanTasks = [];
      this.repeatableCompletedTasks = [];
      this.nonRepeatableCompletedTasks = [];
      this.loading = false;
      this.errorAlertEvent = false;
      this.networkError = {};
      this.showUI = false;
      this.autoRefreshTime = "";
      this.autoRefreshOn = false;
      this.showStageCommentDialog = false;
      this.showRedirectMessageDialog = false;
      this.showRefreshItem = false;
      this.fetchingDataFirstTime = false;
      this.stageComment = "";
      this.lenderRedirectUrl = "";
      this.merchantID = "";
      this.pendingManualTasks = null;
      this.showGoToCoApplicantsOrGuarantorsLoansButton = false;
      this.showGoToParentLoanButton = false
    },

    onToolbarActionSelected(selectedAction) {
      if (ToolbarMenuItems.GO_TO_MERCHANT_APP === selectedAction) {
        this.showRedirectToPartnerDialog();
      }
    },

    showRedirectToPartnerDialog() {
      this.showRedirectMessageDialog = true;
      this.$refs.redirectMessageDialog.initializeDialog();
    },

    onRedirectPartnerApp() {
      if (LoanScopeDataHolder.getRedirectUrl()) {
        window.location = LoanScopeDataHolder.getRedirectUrl();
        return;
      }

      if (PartnerAppParam.isPartnerCallbackWithJavaScriptInterface()) {
        // eslint-disable-next-line no-undef
        partnerCallback.openPartnerApp();
      } else {
        window.location = "https://stage-finance.piggyride.in/app/v1/redirect";
      }
    },

    onRedirectionCancelled() {
      this.showRedirectMessageDialog = false;
      if (this.fetchingDataFirstTime) {
        this.getLoanStages();
      }
    },

    onGoToParentLoan() {
      this.resetData();
      LoanScopeDataHolder.setLoanId(LoanScopeDataHolder.getParentLoanId());
      this.fetchInitialData();
    },

    onGoToCoApplicantsOrGuarantorsLoans() {
      AssociatedEntityDataHolder.setAssociatedEntityDetails(
          LoanScopeDataHolder.getLoanId(),
          null,
          null,
          false,
          [AssociatedEntityType.CO_APPLICANT.rawValue, AssociatedEntityType.GUARANTOR.rawValue],
          null,
          "Co-applicants and Guarantors",
          "Co-applicants and Guarantors",
      );

      ActivityNavigator.navigateTo(Activities.ASSOCIATED_ENTITY_HOME);
    },

    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    startAutoRefreshTimer() {
      this.autoRefreshTime = new Date().getTime() + AUTO_REFRESH_TIME;
      this.autoRefreshOn = true;
    },

    stopAutoRefreshTimer() {
      this.autoRefreshOn = false;
    },

    onAutoRefreshTimeFinished() {
      this.fetchAllRequiredData();
    },

    openTaskOrShowComment(loanTask) {
      LoanScopeDataHolder.setCurrentLoanTask(loanTask);
      if (loanTask.comments) {
        this.stageComment = loanTask.comments;
        this.showStageCommentDialog = true;
      } else {
        this.openTask(loanTask.name);
      }
    },

    openTask(loanTaskName) {
      ActivityNavigator.navigateTo(loanTaskName);
    },

    dismissStageComment() {
      this.hideStageComment();
      LoanScopeDataHolder.setCurrentLoanTask(null);
    },

    hideStageComment() {
      this.showStageCommentDialog = false;
      this.stageComment = "";
    },

    openTaskAndHideCommentDialog() {
      this.hideStageComment();
      let task = LoanScopeDataHolder.getCurrentLoanTask();
      if (task && task.name) {
        this.openTask(task.name);
      }
    },

    getLoanDetailsUsingLoanId() {
      this.loading = true;
      DataSource.getEzLoanById(LoanScopeDataHolder.getLoanId(), true, true)
        .then(ezLoanDataRes => {
          this.loading = false;
          if (ezLoanDataRes.errorOccurred) {
            this.showError(ezLoanDataRes.data);
            if (this.fetchingDataFirstTime) {
              this.showRefreshItem = true;
            }
          } else {
            this.showUI = true;
            this.ezLoan = ezLoanDataRes.data;
            LoanScopeDataHolder.setDetailsFromEzLoan(ezLoanDataRes.data);

            this.showGoToCoApplicantsOrGuarantorsLoansButton =
                EzLoanUtil.isAnyCoApplicantAssociatedEntityPresent(this.ezLoan)
                || EzLoanUtil.isAnyGuarantorAssociatedEntityPresent(this.ezLoan);

            this.showGoToParentLoanButton =
                EzUtils.isValidId(LoanScopeDataHolder.getParentLoanId());

            this.toolbarTitle = LoanScopeDataHolder.isOnboardingLoan()
                ? "Merchant Details" : "Loan Details";

            this.toolbarSubTitle = LoanScopeDataHolder.getLoanAccountNumber();

            this.lenderRedirectUrl = LoanScopeDataHolder.getRedirectUrl();
            if (this.lenderRedirectUrl
                && (LoanScopeDataHolder.isLoanRejected()
                    || LoanScopeDataHolder.isLoanWithdrawnByApplicant())
            ) {
              this.showRedirectToPartnerDialog();
            } else if (LoanScopeDataHolder.isLoanActive()
              && LoanScopeDataHolder.isOnboardingLoan()
              && !this.merchantID
            ) {
              this.getMerchantIdFromLenderLoanDetails();
            } else {
              this.getLoanStages();
            }
          }
        });
    },

    getMerchantIdFromLenderLoanDetails() {
      this.loading = true;
      DataSource.getDataTableInfo(DataTables.LENDER_LOAN_DETAILS, LoanScopeDataHolder.getLoanId())
        .then(lenderLoanDetailsRes => {
          this.loading = false;
          if (lenderLoanDetailsRes.errorOccurred) {
            this.showError(lenderLoanDetailsRes.data);
            if (this.fetchingDataFirstTime) {
              this.showRefreshItem = true;
            }
          } else {
            const lenderLoanDetails = lenderLoanDetailsRes.data;
            lenderLoanDetails && lenderLoanDetails.forEach(lenderLoanDetail => {
              if ("merchant_external_id" === lenderLoanDetail.key) {
                this.merchantID = lenderLoanDetail.value;
              }
            });

            this.getLoanStages();
          }
        })
    },

    getLoanDetailsUsingAccountNumber() {
      this.loading = true;
      DataSource.getLoanAccountDetailsByAccountNumber(LoanScopeDataHolder.getLoanAccountNumber())
        .then(loanDetailsRes => {
          this.loading = false;
          if (loanDetailsRes.errorOccurred) {
            if ("404 Not Found" === loanDetailsRes.data.title) {
              const userName = PrefManager.getUser() ? PrefManager.getUser().username : "";
              this.showError({
                "title": `The account number ${LoanScopeDataHolder.getLoanAccountNumber()} is not associated with ${userName}.`,
                "description": "Please use correct username/mobile number to login or contact support."
              })
            } else {
              this.showError(loanDetailsRes.data);
            }
            if (this.fetchingDataFirstTime) {
              this.showRefreshItem = true;
            }
          } else {
            LoanScopeDataHolder.setLoanId(loanDetailsRes.data.id);
            this.getLoanDetailsUsingLoanId();
          }
        });
    },

    getLoanStages() {
      //TODO remove this if statement code after task type refactoring
      if (!LoanScopeDataHolder.isLoanActive()
        && !this.isAgent
        && "byjus" === LoanScopeDataHolder.getRetailerShortName()
      ) {
        LoanScopeDataHolder.setAllowedTasks([
          Activities.KYC,
          Activities.NACH,
          Activities.SIGN_LENDER_DOCUMENT
        ]);
      }

      this.loading = true;
      this.stopAutoRefreshTimer();
      LoanTasksUtility.getTasks(LoanScopeDataHolder.getLoanId(), false, null)
        .then(tasksRes => {
          this.loading = false;
          let loanTasks;
          if (tasksRes.errorOccurred) {
            this.showError(tasksRes.data);
            loanTasks = [];
          } else {
            loanTasks = tasksRes.data.agentTasks;
            this.updatePendingManualTasks(tasksRes.data.pendingManualTasks);
          }

          const completedTasks =
            EzUtils.filterLoanActionsByStatus(loanTasks, [TaskStatus.COMPLETED]);
          this.pendingLoanTasks =
            EzUtils.filterLoanActionsByStatus(loanTasks, [TaskStatus.PENDING]);
          this.repeatableCompletedTasks = EzUtils.getRepeatableLoanAction(completedTasks);
          this.nonRepeatableCompletedTasks = EzUtils.getNonRepeatableLoanAction(completedTasks);

          if (this.pendingLoanTasks.length === 0
            && (LoanScopeDataHolder.isLoanApproved() || LoanScopeDataHolder.isLoanPending())
          ) {
            this.startAutoRefreshTimer();
          }
          this.$refs.refToolBar.updateLenderCobrandingData();
        })
    },

    fetchAllRequiredData() {
      this.fetchingDataFirstTime = false;
      this.getLoanDetailsUsingLoanId();
    },

    fetchInitialData() {
      this.fetchingDataFirstTime = true;
      this.showRefreshItem = false;
      if (EzUtils.isValidId(LoanScopeDataHolder.getLoanId())) {
        this.getLoanDetailsUsingLoanId();
      } else if (LoanScopeDataHolder.getLoanAccountNumber()) {
        this.getLoanDetailsUsingAccountNumber();
      }
    },

    updatePendingManualTasks(pendingManualTasks) {
      this.pendingManualTasks = "";
      if (EzUtils.isNotEmpty(pendingManualTasks)) {
        let taskNames = [];
        pendingManualTasks.forEach(task => {
          const taskName = task.name;

          const nameFromActivity = Activities.getActivityDescription(taskName);
          if (nameFromActivity && nameFromActivity.title) {
            taskNames.push(nameFromActivity.title);
          } else {
            taskNames.push(EzUtils.removeUnderscoreFromString(taskName));
          }
        });
        this.pendingManualTasks = EzUtils.joinArray(", ", taskNames);
      }
    }
  },

  created: function () {
    LoanScopeDataHolder.setParentLoanId(-1);
    AssociatedEntityDataHolder.clearAssociatedEntityData();
    LoanScopeDataHolder.setCurrentLoanTask(null);
    if (EzUtils.isValidId(LoanScopeDataHolder.getLoanId())
      || LoanScopeDataHolder.getLoanAccountNumber()
    ) {
      this.fetchInitialData();
    } else {
      ActivityNavigator.navigateTo(Activities.HOME);
    }
  },

  computed: {
    needToShowNextActions() {
      return EzUtils.isNotEmpty(this.pendingLoanTasks);
    },

    needToShowRepeatableActions() {
      return EzUtils.isNotEmpty(this.repeatableCompletedTasks);
    },

    needToShowNonRepeatableActions() {
      return EzUtils.isNotEmpty(this.nonRepeatableCompletedTasks);
    },

    toolbarActionsList() {
      const toolbarActions = [
        ToolbarMenuItems.CONTACT_SUPPORT,
        ToolbarMenuItems.LOGOUT,
        ToolbarMenuItems.CLOSE
      ];
      if (PartnerAppParam.isPartnerAppCallback() || this.lenderRedirectUrl) {
        toolbarActions.push(ToolbarMenuItems.GO_TO_MERCHANT_APP);
      }
      return toolbarActions;
    },

    closeActionNavigationActivity() {
      return Activities.HOME;
    },

    isAgent() {
      return PrefManager.doesUserHasRole(RolesConstants.AGENT);
    },

    title() {
      return LoanScopeDataHolder.isOnboardingLoan() ? "Merchant Details" : "Loan Details"
    }
  }
}
</script>

<style scoped src="../../../assets/style/base.css"></style>
